/**
 * F14 — UnitExecutionDashboard (v2).
 * Parallel fetch of 5 reads for the given propertyId:
 *   §2 readiness, §3 execution-stages, §4 procurement-timeline, §5 conflicts, §6 forecast.
 * UI:
 *   - 4 readiness KPI tiles (from §2)
 *   - Stage grid with StageStatusChip + sub-phase chips + hasConflict badge
 *   - Conflicts panel (ruleId / severity / dates)
 *   - Procurement timeline date-grid
 *   - Forecast date-grid
 *   - procurementState==="NO_BOQ" → banner empty-state (not error toast)
 * Guards: propertyId truthy before fetching.
 * Reads: wbsStageId/scope/blockingWbsStageId from stage descriptors (§3/§6).
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getUnitReadiness,
  getUnitExecutionStages,
  getUnitProcurementTimeline,
  getUnitConflicts,
  getUnitForecast,
} from "../../services/interiorExecution/index";
import StageStatusChip from "../../components/InteriorExecution/StageStatusChip";
import { AlertTriangle, CheckCircle, Clock, Layers } from "lucide-react";

const fmt = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const KpiTile = ({ label, value, icon: Icon, tone = "slate" }) => {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    rose:    "bg-rose-50 text-rose-700",
    slate:   "bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-xl p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-3">
        {Icon && <Icon size={20} className="opacity-70" />}
        <div>
          <p className="text-xs font-medium opacity-70">{label}</p>
          <p className="text-xl font-bold">{value ?? "—"}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * @param {{propertyId?: number|string}} props
 *   propertyId may come from parent (tab mount) or useParams (standalone route).
 */
const UnitExecutionDashboard = ({ propertyId: propertyIdProp }) => {
  const { propertyId: propertyIdParam } = useParams();
  const propertyId = propertyIdProp ?? propertyIdParam;
  const [readiness, setReadiness] = useState(null);
  const [stages, setStages] = useState([]);
  const [procurement, setProcurement] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [r, s, p, c, f] = await Promise.all([
          getUnitReadiness(propertyId),
          getUnitExecutionStages(propertyId),
          getUnitProcurementTimeline(propertyId),
          getUnitConflicts(propertyId),
          getUnitForecast(propertyId),
        ]);
        setReadiness(r);
        setStages(Array.isArray(s?.stages) ? s.stages : []);
        setProcurement(p);
        setConflicts(Array.isArray(c?.conflicts) ? c.conflicts : []);
        setForecast(Array.isArray(f?.stages) ? f.stages : []);
      } catch (err) {
        setError("Failed to load execution data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [propertyId]);

  if (!propertyId) {
    return <div className="p-6 text-center text-slate-400">No unit selected.</div>;
  }

  if (loading) {
    return <div className="p-6 text-center text-slate-500 animate-pulse">Loading execution data…</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  // procurementState === "NO_BOQ" → show banner empty-state
  if (procurement?.procurementState === "NO_BOQ") {
    return (
      <div className="p-8 flex flex-col items-center gap-4 text-center">
        <div
          data-testid="no-boq-banner"
          className="rounded-2xl border border-amber-200 bg-amber-50 px-8 py-6 max-w-md"
        >
          <AlertTriangle size={32} className="text-amber-500 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-amber-800 mb-1">No BOQ Configured</h3>
          <p className="text-sm text-amber-700">
            A Bill of Quantities (BOQ) has not been set up for this unit yet. Once the procurement team
            adds the BOQ, execution readiness and timelines will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* KPI Tiles */}
      {readiness && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile
            label="Overall Readiness"
            value={readiness.overallReadiness != null ? `${readiness.overallReadiness}%` : "—"}
            icon={CheckCircle}
            tone={readiness.overallReadiness >= 80 ? "emerald" : "amber"}
          />
          <KpiTile
            label="Stages Complete"
            value={readiness.stagesComplete != null ? readiness.stagesComplete : "—"}
            icon={Layers}
            tone="slate"
          />
          <KpiTile
            label="Days to Possession"
            value={readiness.daysToPossession != null ? readiness.daysToPossession : "—"}
            icon={Clock}
            tone={readiness.daysToPossession <= 30 ? "amber" : "slate"}
          />
          <KpiTile
            label="Open Conflicts"
            value={conflicts.length}
            icon={AlertTriangle}
            tone={conflicts.length > 0 ? "rose" : "emerald"}
          />
        </div>
      )}

      {/* Stage Grid */}
      {stages.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Execution Stages</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Stage</th>
                  <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Status</th>
                  <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Sub-phases</th>
                  <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Start</th>
                  <th className="text-left px-4 py-2.5 text-slate-500 font-medium">End</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stages.map(stage => (
                  <tr key={stage.wbsStageId || stage.stageKey} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-slate-700">
                      {stage.stageLabel || stage.stageKey}
                      {stage.hasConflict && (
                        <span
                          data-testid={`conflict-badge-${stage.stageKey}`}
                          className="ml-2 inline-block w-2 h-2 rounded-full bg-rose-500"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <StageStatusChip status={stage.status} stageKey={stage.stageKey} />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(stage.subPhases || []).map(sub => (
                          <StageStatusChip
                            key={sub.subPhase}
                            status={sub.status}
                            stageKey={stage.stageKey}
                            subPhase={sub.subPhase}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{fmt(stage.startDate)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{fmt(stage.endDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conflicts Panel */}
      {conflicts.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-rose-100">
            <h3 className="text-sm font-semibold text-rose-800">Conflicts</h3>
          </div>
          <div className="divide-y divide-rose-100">
            {conflicts.map((c, idx) => (
              <div
                key={c.ruleId || idx}
                data-testid={`conflict-row-${c.ruleId}`}
                className="px-4 py-3 text-xs"
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-rose-800">{c.ruleId}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    c.severity === "CRITICAL" ? "bg-rose-200 text-rose-900" : "bg-orange-100 text-orange-700"
                  }`}>
                    {c.severity}
                  </span>
                </div>
                <p className="text-slate-700 mt-1">{c.description}</p>
                {(c.startDate || c.endDate) && (
                  <p className="text-slate-500 mt-0.5">{fmt(c.startDate)} – {fmt(c.endDate)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Procurement Timeline */}
      {procurement && procurement.procurementState !== "NO_BOQ" && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Procurement Timeline</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Item</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Order By</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Delivery By</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(procurement.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-slate-700">{item.itemName}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(item.orderByDate)}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(item.deliveryByDate)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        item.status === "ORDERED" ? "bg-emerald-50 text-emerald-700"
                          : item.status === "LATE" ? "bg-rose-50 text-rose-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>{item.status || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Forecast Grid */}
      {forecast.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Forecast</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Stage</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Forecasted Start</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Forecasted End</th>
                  <th className="text-left px-4 py-2 text-slate-500 font-medium">Prerequisite</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {forecast.map((f, idx) => (
                  <tr key={f.wbsStageId || idx}>
                    <td className="px-4 py-2 text-slate-700">{f.stageLabel || f.stageKey}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(f.forecastedStartDate)}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(f.forecastedEndDate)}</td>
                    {/* v2: prerequisiteWbsStageId (not prerequisiteStatusTypeId) */}
                    <td className="px-4 py-2 text-slate-500">{f.prerequisiteWbsStageId || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitExecutionDashboard;
